import React from 'react';
import { useTheme } from 'next-themes';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import { trackCtaClick } from 'helpers/analytics/ga4Events';
import { DARK } from 'helpers/constants/events';
import { Reference } from 'types/reference';
import Column, { FooterColumn } from './atoms/column';
import './footer.css';
import FooterBottom from './footer-bottom';

export interface LegalText {
  textColor?: string;
  textSize?: string;
  textStyle?: string;
  text?: string;
}

export interface FooterLogo {
  primaryLogoLight: ImageProps;
  secondaryLogoLight: ImageProps;
  primaryLogoDark: ImageProps;
  secondaryLogoDark: ImageProps;
  logoLink?: Reference;
  logoLinkGoogle?: Reference;
}

export interface Props {
  columns: FooterColumn[];
  legalText: LegalText;
  footerBgColor: string;
  footerLogo: FooterLogo;
  footerBgImage?: ImageProps;
}

const Footer: React.FC<Props> = ({ legalText, footerLogo, footerBgColor, footerBgImage, columns }) => {
  const { primaryLogoLight, secondaryLogoLight, primaryLogoDark, secondaryLogoDark, logoLink, logoLinkGoogle } =
    footerLogo;

  const { resolvedTheme = 'light' } = useTheme();
  const backgroundStyle: React.CSSProperties = {
    background: footerBgImage
      ? `${footerBgColor || ''} url(${footerBgImage?.media?.file}) center/cover no-repeat`
      : footerBgColor || '',
  };

  const primaryLogo = resolvedTheme == DARK ? primaryLogoDark : primaryLogoLight;
  const secondaryLogo = resolvedTheme == DARK ? secondaryLogoDark : secondaryLogoLight;

  const renderLogo = (logoType: 'primary' | 'secondary', logoImage: ImageProps, logoLink?: Reference) => {
    const imageLogo = <Image {...logoImage} data-ffid={`img-footer-${logoType}-logo`} className={`inline w-full object-scale-down`} cropType='scale' unoptimized/>;
    return logoLink ? (
      <Link
        aria-label="Go to Fujifilm and Google Photos platform home"
        link={logoLink}
        data-ffid={`lnk-footer-${logoType}-logo`}
        data-testid={`footer-link-${logoType}`}
        style={{ width: secondaryLogo?.src ? '50%' : '100%' }}
        onClick={() => {
          const linkUrl = typeof logoLink === 'string' ? logoLink : ((logoLink as any)?.link ?? '');
          trackCtaClick('Footer', `logo_${logoType}`, linkUrl);
        }}
      >
        {imageLogo}
      </Link>
    ) : (
      imageLogo
    );
  };

  return (
    <footer className="container" {...(backgroundStyle ? { style: backgroundStyle } : {})} data-ffid="ctr-footer">
      <div className="bg-white py-24 dark:bg-gmsb-header-gray lg:py-44" data-ffid="ctr-footer-container">
        <div className="w-full justify-between md:flex">
          <div className="grid w-full grid-cols-1 sm:grid-cols-[max-content_1fr_1fr]">
            <div
              className="flex h-30 w-265 flex-row items-center justify-between gap-12 pb-20 sm:flex-row sm:items-stretch sm:justify-stretch sm:gap-16 sm:pr-20 md:pr-40 lg:h-45 lg:w-390"
              data-ffid="ctr-footer-logos"
            >
              {renderLogo('primary', primaryLogo, logoLink)}
              {secondaryLogo?.media && <div className="hidden h-30 w-px bg-gray-400 sm:block"></div>}
              {secondaryLogo?.media && renderLogo('secondary', secondaryLogo, logoLinkGoogle)}
            </div>
            <div className="flex flex-col gap-8 md:flex-row md:justify-between">
              {columns?.map((column, index) => (
                <div key={'col' + index} className="flex" data-ffid={`ctr-footer-column-${index}`}>
                  <Column column={column} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <FooterBottom legalText={legalText} />
      </div>
    </footer>
  );
};

export default Footer;
